import { CommonModule } from '@angular/common';
import { Component, HostListener, computed, effect, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService, ThemeService, DialogService, LoggerService, ApiServiceZonesService, ServiceZoneDto } from '@shared';
import { AdminZoneFilterService } from '../services/admin-zone-filter.service';

interface NavItem {
  label: string;
  route: string;
  /** When set, item is only shown for these roles (within an allowed section). */
  roles?: string[];
  icon:
    | 'dashboard'
    | 'pos'
    | 'orders'
    | 'promotions'
    | 'coupons'
    | 'products'
    | 'categories'
    | 'stores'
    | 'reports'
    | 'settings'
    | 'notifications'
    | 'delivery'
    | 'incidents'
    | 'users'
    | 'parcel'
    | 'analytics'
    | 'serviceZone';
  badge?: string;
  /** When true, `routerLinkActive` only matches the full path (avoids highlighting `/settings` on child routes). */
  exactPath?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
  roles?: string[]; // Optional: if specified, only these roles can see this section
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    items: [{ label: 'Dashboard', route: '/dashboard', icon: 'dashboard' }],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Orders', route: '/orders', icon: 'orders', badge: 'Live' },
      { label: 'Promotions', route: '/promotions', icon: 'promotions' },
      { label: 'Coupons', route: '/coupons', icon: 'coupons', roles: ['superAdmin', 'admin'] },
      { label: 'Notifications', route: '/notifications', icon: 'notifications' },
    ],
  },
  {
    title: 'Delivery Ops',
    items: [
      { label: 'Couriers', route: '/delivery-agents', icon: 'delivery' },
      { label: 'Incidents', route: '/delivery-incidents', icon: 'incidents', badge: 'New' },
    ],
  },  {
    title: 'Catalog',
    items: [
      { label: 'Products', route: '/products', icon: 'products' },
      { label: 'Categories', route: '/categories', icon: 'categories' },
      { label: 'Product Categories', route: '/product-categories', icon: 'categories' },
      { label: 'Stores', route: '/stores', icon: 'stores' },
      { label: 'Vendor reviews', route: '/vendor-reviews', icon: 'reports' },
    ],
  },
  {
    title: 'Insights',
    roles: ['superAdmin', 'admin', 'zoneAdmin'],
    items: [
      { label: 'Reports', route: '/reports', icon: 'reports', roles: ['superAdmin', 'admin'] },
      { label: 'Earnings', route: '/earnings', icon: 'dashboard', roles: ['superAdmin', 'admin'] },
      { label: 'Crash Reports', route: '/crash-reports', icon: 'incidents' },
    ],
  },
  {
    title: 'Grocery Analytics',
    roles: ['admin', 'grocery_ops'], // Admin and Grocery Ops can see Grocery Insights
    items: [
      { label: 'Grocery Insights', route: '/grocery-insights', icon: 'analytics' },
    ],
  },
  {
    title: 'Parcel Delivery',
    roles: ['superAdmin', 'admin'],
    items: [
      { label: 'Parcel Dashboard', route: '/parcel/dashboard', icon: 'parcel' },
      { label: 'Pricing Management', route: '/parcel/pricing', icon: 'settings' },
      { label: 'Driver Verification', route: '/parcel/drivers', icon: 'delivery' },
      { label: 'Parcel Reports', route: '/parcel/reports', icon: 'reports' },
    ],
  },
  {
    title: 'Settings',
    roles: ['superAdmin', 'admin', 'zoneAdmin'],
    items: [
      { label: 'Settings', route: '/settings', icon: 'settings', exactPath: true, roles: ['superAdmin', 'admin'] },
      { label: 'Service zones', route: '/settings/service-zone', icon: 'serviceZone' },
      { label: 'Zone hours', route: '/settings/zone-hours', icon: 'serviceZone' },
      { label: 'Tax Settings', route: '/settings/tax', icon: 'settings' },
      { label: 'Delivery Fee', route: '/settings/delivery-fee', icon: 'settings' },
      { label: 'Delivery COD QR', route: '/settings/delivery-cod-qr', icon: 'settings' },
      { label: 'Order rules', route: '/settings/order', icon: 'settings' },
      { label: 'Support contact', route: '/settings/support-contact', icon: 'settings' },
      // { label: 'POS Locations', route: '/settings/pos-locations', icon: 'settings', roles: ['superAdmin', 'admin'] },
    ],
  },
  {
    title: 'User Management',
    items: [
      { label: 'Users', route: '/users', icon: 'users' },
    ],
  },
  {
    title: 'System',
    roles: ['superAdmin', 'admin'],
    items: [
      { label: 'Audit Logs', route: '/audit-logs', icon: 'reports' },
    ],
  },
];

@Component({
  selector: 'admin-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './admin-shell.component.html',
})
export class AdminShellComponent {
  private themeService = inject(ThemeService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private logger = inject(LoggerService);
  private apiServiceZones = inject(ApiServiceZonesService);
  readonly zoneFilter = inject(AdminZoneFilterService);
  readonly zones = signal<ServiceZoneDto[]>([]);

  /** lg breakpoint — sidebar pinned open at/above this width by default. */
  private static readonly DESKTOP_MIN_WIDTH = 1024;
  readonly isDesktopLayout = signal(
    typeof window !== 'undefined' && window.innerWidth >= AdminShellComponent.DESKTOP_MIN_WIDTH,
  );
  readonly isSidebarPinned = signal(
    typeof window !== 'undefined' && window.innerWidth >= AdminShellComponent.DESKTOP_MIN_WIDTH,
  );
  readonly isMobileDrawerOpen = signal(false);
  readonly isSidebarOpen = computed(() =>
    this.isDesktopLayout() ? this.isSidebarPinned() : this.isMobileDrawerOpen(),
  );
  readonly showSidebarMenuButton = computed(() => !this.isSidebarOpen());
  readonly now = new Date();

  readonly isDark = this.themeService.isDark;
  readonly profile = this.authService.profile;
  readonly userInitials = computed(() => {
    const profile = this.profile();
    if (!profile?.name && !profile?.email) {
      return 'CC';
    }
    if (profile?.name) {
      return profile.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')
        .padEnd(2, 'C');
    }
    return profile.email?.slice(0, 2).toUpperCase() ?? 'CC';
  });

  readonly userRole = computed(() => this.profile()?.role ?? 'admin');
  readonly userEmail = computed(() => this.profile()?.email ?? '');
  readonly showZoneFilter = computed(() => {
    const r = this.profile()?.role;
    return r === 'admin' || r === 'superAdmin';
  });

  /** Zone admins see their assigned zone in the header; it is not editable here. */
  readonly showAssignedZoneReadonly = computed(() => this.profile()?.role === 'zoneAdmin');

  readonly assignedZoneDisplay = computed(() => {
    const id = this.profile()?.serviceZoneId;
    if (!id) {
      return 'Not assigned';
    }
    const z = this.zones().find((x) => x.id === id);
    return z?.name ?? id.slice(0, 8) + '…';
  });

  onZoneChange(e: Event): void {
    const v = (e.target as HTMLSelectElement).value;
    this.zoneFilter.selectZone(v === 'all' ? 'all' : v);
  }

  readonly trackZoneById = (_index: number, z: ServiceZoneDto) => z.id;

  // Filter navigation sections based on user role
  readonly navSections = computed(() => {
    const role = this.userRole();
    const profile = this.profile();
    
    // Debug logging
    if (!profile) {
      console.warn('⚠️ [AdminShell] No user profile found, showing all sections');
      return NAV_SECTIONS;
    }
    
    return NAV_SECTIONS.filter(section => {
      // Hide Parcel Delivery section for now
      if (section.title === 'Parcel Delivery') {
        return false;
      }

      // If section has no role restrictions, show it to everyone
      if (!section.roles || section.roles.length === 0) {
        return true;
      }
      // Otherwise, check if user's role is in the allowed roles
      return section.roles.includes(role);
    }).map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.roles || item.roles.length === 0 || item.roles.includes(role),
      ),
    })).filter((section) => section.items.length > 0);
  });

  constructor() {
    this.handleResize();
    this.apiServiceZones.list().subscribe({
      next: (list: ServiceZoneDto[]) =>
        this.zones.set([...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))),
      error: () => this.zones.set([]),
    });

    effect(() => {
      const profile = this.profile();
      if (profile) {
        this.zoneFilter.syncZoneAdminSelection(profile);
      }
    });
  }

  toggleTheme(): void {
    this.themeService.toggleDarkMode();
  }

  toggleSidebar(): void {
    if (this.isDesktopLayout()) {
      this.isSidebarPinned.update((value) => !value);
      return;
    }
    this.isMobileDrawerOpen.update((open) => !open);
  }

  closeMobileSidebar(): void {
    this.isMobileDrawerOpen.set(false);
  }

  async signOut(): Promise<void> {
    // Show confirmation dialog
    const confirmed = await this.dialogService.confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
    });

    if (!confirmed) {
      this.logger.debug('Sign out cancelled by user');
      return;
    }

    this.logger.debug('Sign out initiated from sidebar');
    try {
      await this.authService.signOut();
      this.logger.debug('Sign out successful, navigating to login');
      
      // Use window.location as fallback if router navigation fails
      try {
        await this.router.navigate(['/login'], { replaceUrl: true });
        this.logger.debug('Navigation complete');
      } catch (navError) {
        this.logger.warn('Router navigation failed, using window.location', navError);
        window.location.href = '/login';
      }
    } catch (error: unknown) {
      // Error will be handled by global error handler
      // Fallback to window.location even on error
      window.location.href = '/login';
    }
  }

  @HostListener('window:resize')
  handleResize(): void {
    const isDesktop = window.innerWidth >= AdminShellComponent.DESKTOP_MIN_WIDTH;
    this.isDesktopLayout.set(isDesktop);
    if (isDesktop) {
      this.isSidebarPinned.set(true);
      this.isMobileDrawerOpen.set(false);
    }
  }

  trackSection(_index: number, section: NavSection): string {
    return section.title;
  }

  trackNavItem(_index: number, item: NavItem): string {
    return item.route;
  }
}
